/**
 * Security Center — Carbon pillar page (frontend-carbon-blueprint §5.2
 * security-bpa, mockup .agents/Mockups/black-redesign-2026-10/carbon/
 * 03-security-center.html).
 *
 * Composition (top to bottom):
 *   1. PillarTabs — all five views as line tabs above the brief (D24), with
 *      their counts (Best Practice, Secure Score, App Posture, SCuBA Deep).
 *   2. The brief. On Overview it is this page's PillarBrief over the SAME
 *      Guardian Score composite the Command Center renders (there is no
 *      separate "Security score"); every other tab renders its own
 *      pillar-specific brief, so this one is not stacked on top of them.
 *   3. The tab body. Overview: the three things to fix first, open findings
 *      by framework and severity, then the Best Practice top items (the
 *      control table the brief's primary action opens, id="controls") beside
 *      the Secure Score top recommendations.
 *
 * Hard preserves: all five existing tab body components stay intact, the
 * framework star toggle inside <BestPracticeAnalyzerTab> is untouched,
 * severity / category / tier filters inside child tabs are untouched, the
 * Secure Score history chart and SCuBA control drilldown are untouched, and
 * tab URL sync (`?tab=…`) keeps its existing query keys.
 *
 * Source-of-truth references:
 *   - .agents/Tasks/High Priority/frontend-carbon-blueprint.md §5.2, §6, §7
 *   - DESIGN.md, CLAUDE.md "Product Voice"
 */

import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api'
import {
  calculateStarredBpaScore,
  calculateUnifiedGuardianScore,
  useComplianceDashboard,
  useComplianceScoreHistory,
} from '@/hooks/useComplianceData'
import { useTenantContext } from '@/hooks/useTenantContext'
import { usePostureBrief } from '@/hooks/usePostureBrief'
import { useTenantScanSchedule } from '@/hooks/useTenantScanSchedule'
import { canEditScanSchedule } from '@/lib/scan-schedule'
import { useSecurityBrief } from '@/hooks/useSecurityBrief'
import { usePriorityActions } from '@/hooks/usePriorityActions'
import { guardianComponents } from '@/components/Dashboard/v2/cc-score'
import { assessmentProvenance } from '@/components/Compliance/assessment-provenance'
import { refMatchesFramework, pickPrimaryFrameworkRef } from '@/lib/frameworkUtils'
import type { PriorityAction, Severity } from '@/lib/posture-brief'
import type {
  ComplianceTrendPoint,
  ComplianceViolation,
  FrameworkScoreDisplay,
  SecureScoreControlProfile,
  SecureScoreSummary,
} from '@/types'

import PillarBrief from '@/components/PostureBrief/PillarBrief'
import PillarTabs, { usePillarTab } from '@/components/PostureBrief/PillarTabs'
import PriorityQueue from '@/components/PostureBrief/PriorityQueue'
import EditorialHeatmap, { type HeatmapRow, type Intensity } from '@/components/PostureBrief/EditorialHeatmap'
import { SectionHeadline } from '@/components/PostureBrief/SectionHeadline'
import ScoreDeltaChip from '@/components/Dashboard/v2/ScoreDeltaChip'
import EmptyState from '@/components/shared/EmptyState'
import { Button } from '@/components/carbon/Button'
import { CondensedGrid } from '@/components/carbon/CondensedGrid'
import { DataTable, TableItem, type DataColumn } from '@/components/carbon/DataTable'
import { IconArrowRight } from '@/components/carbon/icons'
import { SeverityGlyph } from '@/components/carbon/SeverityGlyph'
import { Skeleton } from '@/components/carbon/Skeleton'
import { Tag } from '@/components/carbon/Tag'
import { Tile } from '@/components/carbon/Tile'
import type { SeverityLevel } from '@/components/carbon/types'

import {
  AppPostureTab,
  BestPracticeAnalyzerTab,
  ScubaDeepScanTab,
  SecureScoreTab,
} from '@/components/SecurityCenter'
import type { OAuthAppSummary } from '@/components/SecurityCenter/appPermissionsTypes'

/* -------------------------------------------------------------------------- */
/* Tab definitions                                                            */
/* -------------------------------------------------------------------------- */

type TabKey =
  | 'overview'
  | 'best-practices'
  | 'secure-score'
  | 'scuba'
  | 'app-permissions'

// App Posture (id retained as 'app-permissions' for URL stability) sits before
// SCuBA Deep — App Posture is universally relevant; SCuBA is CISA-specific and
// deeper-cut, so it sits last. See security-center-subpages-redesign.md §13.
const ALL_TAB_IDS: ReadonlyArray<TabKey> = [
  'overview',
  'best-practices',
  'secure-score',
  'app-permissions',
  'scuba',
] as const

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

export default function SecurityCenter() {
  const navigate = useNavigate()
  const { defaultTenantId, isLoading: isLoadingTenant } = useTenantContext()
  const [selectedTenantId, setSelectedTenantId] = useState<string | undefined>(undefined)

  // Initialize selectedTenantId from context. The sidebar tenant switcher
  // owns tenant switching globally — when the user changes tenants there it
  // triggers a page reload, so we only need to sync once on mount.
  useEffect(() => {
    if (!isLoadingTenant && defaultTenantId !== undefined) {
      setSelectedTenantId(defaultTenantId)
    }
  }, [isLoadingTenant, defaultTenantId])

  /* ------------------------- Tab + URL sync -------------------------------- */
  // PillarTabs handles URL sync internally. The page reads the active tab via
  // usePillarTab and renders only the matching panel — same one-source-of-truth
  // pattern the Command Center uses for Posture / Operations / History.
  const [activeTab, setActiveTab] = usePillarTab<TabKey>(
    ALL_TAB_IDS,
    'overview',
  )

  /* ------------------------- Data queries ---------------------------------- */
  // Each hook below already validates the tenant id and won't fire without
  // it — tenant isolation is honored without any additional gating here.

  const { data: dashboardStats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard', selectedTenantId],
    queryFn: () =>
      selectedTenantId ? api.getDashboardStats(selectedTenantId) : Promise.resolve(null),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  const { data: secureScoreSummary, isLoading: secureScoreLoading } = useQuery<
    SecureScoreSummary | null
  >({
    queryKey: ['secureScoreSummary', selectedTenantId],
    queryFn: () =>
      selectedTenantId ? api.getSecureScoreSummary(selectedTenantId) : Promise.resolve(null),
    enabled: !!selectedTenantId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const { data: userInsights, isLoading: insightsLoading } = useQuery({
    queryKey: ['user-insights', selectedTenantId],
    queryFn: () => api.getUserInsights(selectedTenantId!),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  // Compliance dashboard returns frameworks, violations and the violation
  // summary in a single hook. We rely on it both for the brief and for the
  // overview sections below.
  const {
    dashboardData,
    violations,
    isLoading: complianceLoading,
    isError: complianceError,
  } = useComplianceDashboard(selectedTenantId)

  // Score history powers the framework table's 7-day delta.
  const { data: scoreHistory } = useComplianceScoreHistory(selectedTenantId, 14)

  // Curated BPA priority ranking — single fetch backs both the three-risks
  // tiles (1 and 2) AND the Best Practice top items (top 4). The server-side
  // ranking already accounts for severity x weight x scope so we don't re-sort
  // on the client. See unified-ranking-score-delta-rollout.md §Phase 2.
  const { actions: priorityBpaActions, isLoading: priorityBpaLoading } = usePriorityActions({
    tenantId: selectedTenantId,
    maxCount: 4,
    pillar: 'bpa',
  })

  // OAuth apps summary drives the App Posture tab count AND the third risk
  // tile. We only need the summary counts here; the full app list is fetched
  // lazily on the App Posture tab.
  const { data: oauthSummary } = useQuery<OAuthAppSummary>({
    queryKey: ['oauth-apps-summary', selectedTenantId],
    queryFn: () => api.getOAuthAppSummary(selectedTenantId!),
    enabled: !!selectedTenantId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  // Top tier-1 OAuth app — just the highest-criticality app. We pull a single
  // page of Critical-tier apps; this is the same shape the App Posture
  // tab itself paginates, so React Query de-dupes when the user switches tabs.
  const { data: criticalOauthApps } = useQuery({
    queryKey: ['oauth-apps-tier1-card', selectedTenantId],
    queryFn: () =>
      api.getOAuthApps(selectedTenantId!, 1, 5, 'Critical', undefined, undefined, undefined, undefined),
    enabled: !!selectedTenantId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  /* ------------------------- Score composite ------------------------------- */
  // Same starred-frameworks computation the Command Center uses. Surface the
  // SAME Guardian Score here — there is no Security-pillar-specific composite.
  const bpaStarredScore = useMemo(() => {
    const standardScores = dashboardData?.frameworks
    if (!standardScores) return undefined
    const mapped = standardScores.map(s => ({
      code: s.code,
      score: s.score,
      weight: s.weight,
      includeInGuardianScore: s.includeInGuardianScore,
    }))
    return calculateStarredBpaScore(mapped) ?? undefined
  }, [dashboardData?.frameworks])

  // The four components, each null while unknown, via the Command Center's
  // helper: a baseline with no checked resource (`totalChecks` 0, also the
  // stats error fallback) and an MFA summary without data are unknown, never
  // a 0 that drags the composite down or reads as a real score.
  const components = useMemo(
    () =>
      guardianComponents({
        stats: dashboardStats,
        insights: userInsights,
        bpa: bpaStarredScore,
        secure: secureScoreSummary?.score?.percentage,
      }),
    [dashboardStats, userInsights, bpaStarredScore, secureScoreSummary?.score?.percentage],
  )

  const guardianScore = useMemo(
    () =>
      calculateUnifiedGuardianScore({
        secureScore: components.secure,
        bpaScore: components.bpa,
        mfaAdoption: components.identity,
        configCompliance: components.baseline,
      }),
    [components],
  )

  // The four-part Guardian Score breakdown, accepted by PillarBrief for
  // compatibility (the Carbon tile draws no breakdown). Labels and weights
  // mirror Dashboard.tsx; the fills are the pillar mark tokens.
  const compositeBars = useMemo(
    () => [
      { label: 'Baseline', weight: '20%', value: Math.round(components.baseline ?? 0), fillClass: 'bg-cat-baseline' },
      { label: 'Best Practice', weight: '30%', value: Math.round(components.bpa ?? 0), fillClass: 'bg-cat-bpa' },
      { label: 'MFA', weight: '20%', value: Math.round(components.identity ?? 0), fillClass: 'bg-cat-identity' },
      { label: 'Secure Score', weight: '30%', value: Math.round(components.secure ?? 0), fillClass: 'bg-cat-secure' },
    ],
    [components],
  )

  /* ------------------------- Brief inputs ---------------------------------- */
  // The brief is over Guardian Score, so only violations whose frameworks
  // actually contribute to the score are eligible to be cited as the top
  // action. Unstarred frameworks (`includeInGuardianScore=false`) are
  // intentionally excluded — citing them would tell the user "fix this to lift
  // the score" when fixing it wouldn't move the score at all.
  const starredFrameworkCodes = useMemo(
    () =>
      (dashboardData?.frameworks ?? [])
        .filter(f => f.includeInGuardianScore)
        .map(f => f.code),
    [dashboardData?.frameworks],
  )

  const scoringViolations = useMemo(() => {
    if (starredFrameworkCodes.length === 0) return []
    return violations.filter(v => {
      const refs = v.frameworkReferences ?? []
      return starredFrameworkCodes.some(code =>
        refs.some(ref => refMatchesFramework(ref, code)),
      )
    })
  }, [violations, starredFrameworkCodes])

  // Open SHALL violations restricted to score-driving (starred) frameworks.
  // Drives the Best Practice tab count and the top-action fallback.
  const openShallViolations = useMemo(
    () =>
      scoringViolations.filter(v => v.status === 'Open' && v.criticality === 'SHALL'),
    [scoringViolations],
  )

  // Top SHALL violation feeds the brief's named-risk slot. Sourced from the
  // server-curated priority ranking (same source as risk tile 1 below) so
  // the brief and the tile name the same fix. Picking the first item
  // out of `openShallViolations` instead would land on whatever order the
  // API returns and let the brief disagree with the queue. We still look up
  // the matching violation record by policyCode so the framework citation
  // stays precise (e.g. "MCSB PA-1", not just "MCSB").
  const topRankedAction = priorityBpaActions[0]
  const topRankedPolicyCode =
    topRankedAction?.id.startsWith('bpa:')
      ? topRankedAction.id.slice('bpa:'.length)
      : null
  const topShallViolation =
    (topRankedPolicyCode
      ? openShallViolations.find(v => v.policyCode === topRankedPolicyCode)
      : undefined)
    ?? openShallViolations[0]

  // Counts come from the violation summary (unpaged, starred scope). They are
  // `undefined` unless that query or the list succeeded (`totalViolationsKnown`),
  // never a fabricated 0 that would read as an all-clear (blueprint §5.2
  // wiring note).
  const countsKnown = dashboardData?.totalViolationsKnown === true
  const brief = useSecurityBrief({
    tenantId: selectedTenantId,
    score: guardianScore,
    criticalCount: countsKnown ? dashboardData?.criticalViolations : undefined,
    failingCount: countsKnown ? dashboardData?.totalViolations : undefined,
    // The Best Practice top items table on the Overview (id="controls").
    primaryHref: '#controls',
    topAction: (topRankedAction || topShallViolation)
      ? {
          // Only the ranker names the first item. The headline leads with this
          // title, so a fallback from `openShallViolations` (API order) would
          // reorder the queue. With no ranked item, the critical count leads.
          title: topRankedAction?.title ?? '',
          framework: topShallViolation
            ? pickPrimaryFrameworkRef(
                topShallViolation.frameworkReferences,
                starredFrameworkCodes,
              ) ?? undefined
            : undefined,
          criticality: 'SHALL',
        }
      : undefined,
  })

  const briefLoading =
    statsLoading || secureScoreLoading || insightsLoading || complianceLoading

  // Nothing assessed yet: every component is unknown once the queries settle.
  // The security composer reads a null score as "still loading", so this state
  // takes the posture composer's no-data brief, the same line the Command
  // Center shows for the same tenant.
  const noData = !briefLoading && guardianScore == null
  // Only a Platform Admin may trigger a scan, so only that role gets the Ad-Hoc
  // Scans link; anyone else is told what turns the first assessment on, from
  // the tenant's schedule (finding 5), exactly as on the Command Center. Same
  // cached /me read as useTenantContext.
  const { data: currentUser, isLoading: currentUserLoading } = useQuery({
    queryKey: ['currentUser'],
    queryFn: api.getCurrentUser,
    staleTime: 5 * 60 * 1000,
  })
  const canRunScans = currentUser?.isPlatformAdmin === true
  const scanSchedule = useTenantScanSchedule({
    tenantId: selectedTenantId,
    neverScanned: noData,
    currentUser,
    currentUserLoading,
  })
  const noDataBrief = usePostureBrief({
    tenantId: selectedTenantId,
    noData: true,
    primaryHref: canRunScans ? '/admin/scans' : undefined,
    scheduleSettingsHref: noData && !canRunScans && canEditScanSchedule(currentUser) ? '/tenants' : undefined,
    scanSchedule,
  })

  // D21's provenance: the last Best Practice evaluation and the controls it
  // checked. When the compliance read failed there is nothing to attribute,
  // and "Awaiting first assessment" would be untrue, so the line is dropped
  // (`false`, not undefined, which would fall back to the composer byline).
  const provenance = complianceError
    ? false
    : assessmentProvenance({ at: dashboardData?.lastEvaluatedAt, controls: dashboardData?.totalControls })

  /* ------------------------- Tab counts ------------------------------------ */
  // Each count is derived from existing data — no new aggregator endpoints.
  const bpaShallCount = openShallViolations.length

  // Secure Score "open recommendations" — count items not yet implemented.
  // Uses the same definition the existing RecommendationsList default filter
  // uses (state: 'notImplemented').
  const secureScoreOpen = useMemo(() => {
    const recs = secureScoreSummary?.recommendations ?? []
    return recs.filter(r => !r.isImplemented).length
  }, [secureScoreSummary?.recommendations])

  // SCuBA open count: lazily fetched on the SCuBA tab itself. We surface the
  // total failures count as a reasonable proxy when summary is cached.
  const { data: scubaSummary } = useQuery({
    queryKey: ['scuba-summary', selectedTenantId],
    queryFn: () => api.getScubaSummary(selectedTenantId!),
    enabled: !!selectedTenantId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
  const scubaOpenCount = scubaSummary?.totalFailures ?? null

  const tier1AppCount =
    (oauthSummary?.criticalCount ?? 0) + (oauthSummary?.highCount ?? 0)

  const tabs = useMemo(
    () => [
      { id: 'overview' as TabKey, label: 'Overview', badge: null },
      {
        id: 'best-practices' as TabKey,
        label: 'Best Practice',
        badge: bpaShallCount > 0 ? bpaShallCount : null,
      },
      {
        id: 'secure-score' as TabKey,
        label: 'Secure Score',
        badge: secureScoreOpen > 0 ? secureScoreOpen : null,
      },
      {
        id: 'app-permissions' as TabKey,
        label: 'App Posture',
        badge: tier1AppCount > 0 ? tier1AppCount : null,
      },
      {
        id: 'scuba' as TabKey,
        label: 'SCuBA Deep',
        badge: scubaOpenCount && scubaOpenCount > 0 ? scubaOpenCount : null,
      },
    ],
    [bpaShallCount, secureScoreOpen, scubaOpenCount, tier1AppCount],
  )

  /* ------------------------- Render ---------------------------------------- */
  return (
    <div className="min-w-0">
      {/* Line tabs above the brief (mockup 03): page sub-navigation. */}
      <PillarTabs<TabKey>
        tabs={tabs}
        defaultTab="overview"
        tone="bpa"
        ariaLabel="Security view"
        placement="above-brief"
        actions={
          <Button kind="ghost" size="sm" onClick={() => navigate('/dashboard')}>
            Back to Command Center
          </Button>
        }
      />

      {/* Only the Overview tab uses the page-level Guardian Score brief. The
          other four tabs (best-practices, secure-score, app-permissions,
          scuba) each render their own pillar-specific PillarBrief — stacking
          the page brief on top would double the header. SCuBA in particular
          doesn't roll into Guardian Score, so a Guardian composite brief
          would mis-frame the page. */}
      {activeTab === 'overview' && (
        <PillarBrief
          brief={noData ? noDataBrief : brief}
          score={guardianScore}
          components={compositeBars}
          tone="bpa"
          isLoading={briefLoading}
          // The page loads no score history, so the composer's default
          // "No change in 30 days" would be an invented trend.
          hideDelta
          scoreMeta={noData ? 'No data' : undefined}
          provenance={provenance}
        />
      )}

      <div>
        {activeTab === 'overview' && (
          <div id="panel-overview" role="tabpanel" aria-labelledby="tab-overview">
            <OverviewTab
              violations={violations}
              frameworks={dashboardData?.frameworks ?? []}
              scoreHistory={scoreHistory ?? []}
              secureScoreSummary={secureScoreSummary ?? null}
              criticalOauthApp={criticalOauthApps?.items?.[0]}
              isLoading={complianceLoading}
              priorityBpaActions={priorityBpaActions}
              priorityBpaLoading={priorityBpaLoading}
              onSwitchTab={setActiveTab}
            />
          </div>
        )}

        {activeTab === 'best-practices' && (
          <div id="panel-best-practices" role="tabpanel" aria-labelledby="tab-best-practices">
            <BestPracticeAnalyzerTab tenantId={selectedTenantId} />
          </div>
        )}

        {activeTab === 'secure-score' && (
          <div id="panel-secure-score" role="tabpanel" aria-labelledby="tab-secure-score">
            {selectedTenantId ? (
              <SecureScoreTab tenantId={selectedTenantId} />
            ) : (
              <div className="mt-8 bg-layer-1 px-4">
                <EmptyState
                  headline="Select a tenant to see its Secure Score."
                  body="Use the tenant switcher in the side navigation."
                />
              </div>
            )}
          </div>
        )}

        {activeTab === 'scuba' && (
          <div id="panel-scuba" role="tabpanel" aria-labelledby="tab-scuba">
            <ScubaDeepScanTab tenantId={selectedTenantId} />
          </div>
        )}

        {activeTab === 'app-permissions' && (
          <div id="panel-app-permissions" role="tabpanel" aria-labelledby="tab-app-permissions">
            <AppPostureTab tenantId={selectedTenantId} />
          </div>
        )}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Overview tab — three risks, frameworks by severity, the two top lists      */
/* -------------------------------------------------------------------------- */

interface OverviewTabProps {
  /** All open + closed violations — used by the framework table so that
   *  unstarred frameworks still show their per-framework counts (the table
   *  is intentionally cross-framework and says which rows drive Guardian
   *  Score). */
  violations: ComplianceViolation[]
  frameworks: FrameworkScoreDisplay[]
  scoreHistory: ComplianceTrendPoint[]
  secureScoreSummary: SecureScoreSummary | null
  criticalOauthApp:
    | {
        id: string
        displayName: string
        publisherName: string | null
        isVerifiedPublisher: boolean
        criticalPermissionCount: number
        highPermissionCount: number
      }
    | undefined
  isLoading: boolean
  /** Curated BPA priority actions. Backs the risk tiles (1 and 2) and the
   *  Best Practice top items. Comes from `usePriorityActions({ pillar: 'bpa' })`
   *  in the page component so a single fetch feeds both surfaces. */
  priorityBpaActions: PriorityAction[]
  priorityBpaLoading: boolean
  onSwitchTab: (next: TabKey) => void
}

function OverviewTab({
  violations,
  frameworks,
  scoreHistory,
  secureScoreSummary,
  criticalOauthApp,
  isLoading,
  priorityBpaActions,
  priorityBpaLoading,
  onSwitchTab,
}: OverviewTabProps) {
  const navigate = useNavigate()

  /* -------- Top three risks --------------------------------------------- */
  // Pulled from the curated server-side ranking. The ranking already weighs
  // severity x policy_weight x frameworks x log(scope+1) and respects the
  // starred-framework filter, so the first two tiles are "what to fix first
  // to lift the score" by construction.
  const top1 = priorityBpaActions[0]
  const top2 = priorityBpaActions[1]

  /* -------- Framework rows ---------------------------------------------- */
  // Mirror the BPA tab / PillarBpa rule: only score-driving frameworks
  // appear. Unstarred frameworks would dilute the table with rows the user
  // has explicitly excluded from Guardian Score. MCSB is always included.
  const heatmapRows = useMemo<HeatmapRow[]>(() => {
    const visible = frameworks.filter(
      fw => fw.includeInGuardianScore || (fw.code ?? '').toUpperCase() === 'MCSB',
    )
    return visible.map(fw => {
      // refMatchesFramework already handles SCuBA/CIS/NIST/etc pattern aliasing.
      const matches = (v: ComplianceViolation) =>
        (v.frameworkReferences ?? []).some(ref => refMatchesFramework(ref, fw.code))

      const open = violations.filter(v => v.status === 'Open' && matches(v))
      const shall  = open.filter(v => v.criticality === 'SHALL').length
      const should = open.filter(v => v.criticality === 'SHOULD').length
      const may    = open.filter(v => v.criticality === 'MAY').length

      // 7-day delta — diff today's framework score against the closest point
      // ~7 days ago. ComplianceTrendPoint groups by date and exposes per-
      // framework values via `frameworkScores: { code, score }[]`.
      const delta7d = compute7dFrameworkDelta(scoreHistory, fw.code, fw.score)
      const controls = fw.totalControls

      return {
        label: shortFrameworkLabel(fw.code, fw.name),
        sublabel: `${controls} control${controls === 1 ? '' : 's'} · ${fw.includeInGuardianScore ? 'in' : 'not in'} your Guardian Score`,
        cells: [
          intensityCell(shall, true),
          intensityCell(should, false),
          intensityCell(may, false),
          { value: fw.dataUnavailableControls, intensity: 'l0' as Intensity },
        ],
        health: { score: fw.score, delta: delta7d },
      }
    })
  }, [frameworks, violations, scoreHistory])

  /* -------- Top BPA items + Top Secure Score recs ----------------------- */
  // Same source as top1/top2 above — both surfaces share the curated ranking
  // and the same fetch (capped at 4 in the page-level hook).
  const topBpaItems = priorityBpaActions.slice(0, 4)

  const topSecureScoreRecs = useMemo(() => {
    const recs = secureScoreSummary?.recommendations ?? []
    // Highest pt-impact open recommendations first. Pt impact for Secure Score
    // is real: maxScore - achievedScore for each control profile.
    return [...recs]
      .filter(r => !r.isImplemented)
      .map(r => ({ rec: r, gap: Math.max(0, (r.maxScore ?? 0) - (r.achievedScore ?? 0)) }))
      .sort((a, b) => b.gap - a.gap)
      .slice(0, 4)
  }, [secureScoreSummary?.recommendations])

  // The brief's primary action links "#controls". The router doesn't scroll
  // to a hash, so do it once the table is on the page.
  const { hash, key: locationKey } = useLocation()
  const ready = !isLoading && !priorityBpaLoading
  useEffect(() => {
    if (hash !== '#controls' || !ready) return
    const raf = requestAnimationFrame(() => {
      document.getElementById('controls')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
    return () => cancelAnimationFrame(raf)
  }, [hash, locationKey, ready])

  // Nothing assessed yet: no Best Practice frameworks, no Secure Score and no
  // ranked actions. Say so once instead of drawing empty sections. The brief
  // above carries the action; this names what fills in, and claims no
  // assessment is running or scheduled (nothing in the data says so).
  const noData =
    !isLoading && !priorityBpaLoading && frameworks.length === 0 && !secureScoreSummary && priorityBpaActions.length === 0
  if (noData) {
    return (
      <div className="mt-12 bg-layer-1 px-4">
        <EmptyState
          headline="Findings show here after the first assessment."
          body="Best Practice findings and Secure Score recommendations appear once Guardian has assessed this tenant."
        />
      </div>
    )
  }

  /* -------- Render ------------------------------------------------------- */
  return (
    <div className="mt-12 min-w-0 space-y-12">
      {/* The three things to fix first */}
      <section aria-labelledby="risks-headline">
        <div id="risks-headline" className="mb-6">
          <SectionHeadline>Three things to fix first.</SectionHeadline>
        </div>

        <CondensedGrid as="ol" columns={{ base: 1, 'c-lg': 3 }}>
          <RiskTile
            action={top1}
            ctaLabel="Open Best Practice"
            onCta={() => onSwitchTab('best-practices')}
            isLoading={isLoading || priorityBpaLoading}
          />
          <RiskTile
            action={top2}
            ctaLabel="Open Best Practice"
            onCta={() => onSwitchTab('best-practices')}
            isLoading={isLoading || priorityBpaLoading}
          />
          <AppRiskTile
            app={criticalOauthApp}
            onCta={() => onSwitchTab('app-permissions')}
          />
        </CondensedGrid>
      </section>

      {/* Open findings by framework and severity */}
      <section aria-labelledby="heatmap-headline">
        <div id="heatmap-headline" className="mb-6">
          <SectionHeadline>Open findings by framework and severity.</SectionHeadline>
          <p className="m-0 mt-1 max-w-[72ch] text-body text-text-secondary">
            The frameworks in your Guardian Score, with each one&rsquo;s score and its change over 7 days.
          </p>
        </div>

        {heatmapRows.length === 0 ? (
          <div className="bg-layer-1 px-4">
            <EmptyState
              headline="Star a framework to see it here."
              body="Frameworks join this view once they are in your Guardian Score. Choose them on the Best Practice tab."
              cta={{ label: 'Open Best Practice', to: '/security?tab=best-practices' }}
            />
          </div>
        ) : (
          <EditorialHeatmap
            rows={heatmapRows}
            columns={['Critical', 'High', 'Medium', 'No data']}
            labelHeader="Framework"
            healthHeader="Score"
            ariaLabel="Open findings by framework and severity"
          />
        )}
      </section>

      {/* Best Practice top items (the brief's #controls) + Secure Score top recs */}
      <section className="grid grid-cols-1 gap-12 c-xlg:grid-cols-2 c-xlg:gap-8">
        <div id="controls" className="min-w-0 scroll-mt-[calc(var(--pillar-tabs-h)+1rem)]">
          <PriorityQueue
            title="Best Practice top items"
            description="The curated ranking: severity, weight and scope."
            actions={topBpaItems}
            onItemClick={(action) => navigate(action.href)}
            emptyState={priorityBpaLoading && topBpaItems.length === 0 ? 'Loading top items.' : 'Nothing to review.'}
            footer={
              <Button kind="ghost" size="sm" icon={<IconArrowRight />} onClick={() => onSwitchTab('best-practices')} className="-ml-3">
                Open Best Practice
              </Button>
            }
          />
        </div>
        <SecureScoreTopRecs
          recs={topSecureScoreRecs}
          summary={secureScoreSummary}
          onOpen={() => onSwitchTab('secure-score')}
        />
      </section>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/** Compute the 7-day score delta for one framework from the trend-point series.
 *  Falls back to `null` when the history is too short to compare honestly —
 *  matching the pattern PillarBpa uses for its 7d trend. */
function compute7dFrameworkDelta(
  history: ComplianceTrendPoint[],
  frameworkCode: string,
  currentScore: number,
): number | null {
  if (!history || history.length === 0) return null

  // We want the score from a point ~7 days ago (or the oldest point if the
  // tenant doesn't have that much history yet).
  const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000
  const sorted = [...history].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  )

  let baseline: number | null = null
  for (const point of sorted) {
    if (new Date(point.date).getTime() <= sevenDaysAgo) {
      const fw = point.frameworkScores.find(f => f.code === frameworkCode)
      if (fw) baseline = fw.score
    } else {
      break
    }
  }

  // Fall back to the oldest point we DO have, so a brand-new tenant still
  // sees something instead of a silent dash.
  if (baseline === null) {
    for (const point of sorted) {
      const fw = point.frameworkScores.find(f => f.code === frameworkCode)
      if (fw) {
        baseline = fw.score
        break
      }
    }
  }

  if (baseline === null) return null
  return Math.round(currentScore - baseline)
}

/** An open-finding count as a heatmap cell (D13): a neutral ramp, and only a
 *  critical column with 5 or more items takes the Coral top bucket. */
function intensityCell(count: number, isShall: boolean): { value: number; intensity: Intensity; crit?: boolean } {
  if (count === 0) return { value: count, intensity: 'l0' }
  if (isShall) {
    if (count >= 5) return { value: count, intensity: 'l4', crit: true }
    if (count >= 3) return { value: count, intensity: 'l4' }
    return { value: count, intensity: 'l3' }
  }
  if (count >= 7) return { value: count, intensity: 'l3' }
  if (count >= 3) return { value: count, intensity: 'l2' }
  return { value: count, intensity: 'l1' }
}

/** Map a curated PriorityAction.severity (5 levels) onto the four Carbon
 *  severity levels. 'routine' reads as low. */
function severityLevel(severity: Severity): SeverityLevel {
  switch (severity) {
    case 'crit': return 'crit'
    case 'high': return 'high'
    case 'med':  return 'med'
    case 'low':
    case 'routine':
    default:     return 'low'
  }
}

function shortFrameworkLabel(code: string, name: string): string {
  // Keep the table rows consistent without bulldozing the backend display
  // name. Long codes (NIST_800_53 etc) get a short form; everything else
  // falls through to the friendly name.
  const upper = code.toUpperCase()
  if (upper.startsWith('NIST_800_53')) return 'NIST 800-53'
  if (upper.startsWith('NIST_CSF')) return 'NIST CSF'
  if (upper === 'SCUBA') return 'SCuBA'
  if (upper === 'MCSB') return 'MCSB'
  if (upper === 'CIS') return 'CIS Microsoft 365'
  if (upper === 'EIDSCA') return 'EIDSCA'
  return name || code
}

/* -------------------------------------------------------------------------- */
/* Risk tiles                                                                 */
/* -------------------------------------------------------------------------- */

function RiskTile({
  action,
  ctaLabel,
  onCta,
  isLoading,
}: {
  action: PriorityAction | undefined
  ctaLabel: string
  onCta: () => void
  isLoading: boolean
}) {
  if (!action) {
    return (
      <Tile as="li" className="min-w-0 p-6">
        {isLoading ? (
          <Skeleton lines={3} label="Loading risks" />
        ) : (
          <p className="m-0 text-body text-text-secondary">No open priority in this slot.</p>
        )}
      </Tile>
    )
  }

  const affected = action.scope ?? null

  return (
    <Tile as="li" className="flex min-w-0 flex-col p-6">
      <SeverityGlyph level={severityLevel(action.severity)} />
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <h3 className="m-0 text-body font-semibold text-text-primary">{action.title}</h3>
        <ScoreDeltaChip delta={action.guardianScoreDelta} />
      </div>
      <p className="m-0 mt-2 text-body text-text-secondary">{action.meta}</p>
      <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-6">
        <span className="text-helper text-text-helper">
          {affected != null
            ? `${affected} ${affected === 1 ? 'resource' : 'resources'} affected`
            : 'Best Practice'}
        </span>
        <Button kind="ghost" size="sm" icon={<IconArrowRight />} onClick={onCta} className="-mr-3">
          {ctaLabel}
        </Button>
      </div>
    </Tile>
  )
}

function AppRiskTile({
  app,
  onCta,
}: {
  app: OverviewTabProps['criticalOauthApp']
  onCta: () => void
}) {
  if (!app) {
    return (
      <Tile as="li" className="min-w-0 p-6">
        <p className="m-0 text-body text-text-secondary">No tier-one app permission risks today.</p>
      </Tile>
    )
  }

  const tierCount = (app.criticalPermissionCount ?? 0) + (app.highPermissionCount ?? 0)
  const publisher = app.publisherName || 'unverified publisher'

  return (
    <Tile as="li" className="flex min-w-0 flex-col p-6">
      <Tag size="sm" className="self-start">
        Tier-one app
      </Tag>
      <h3 className="m-0 mt-3 text-body font-semibold text-text-primary">
        {app.displayName || 'Unverified application'}
      </h3>
      <p className="m-0 mt-2 text-body text-text-secondary">
        {app.isVerifiedPublisher
          ? `Verified publisher (${publisher}) holds high-privilege scopes. Review and revoke if not part of an active integration.`
          : `Unverified publisher (${publisher}) holds high-privilege scopes. Review and revoke if not part of an active integration.`}
      </p>
      <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-6">
        <span className="text-helper text-text-helper">
          {tierCount > 0
            ? `${tierCount} ${tierCount === 1 ? 'permission' : 'permissions'} affected · `
            : ''}
          App permissions, tier one
        </span>
        <Button kind="ghost" size="sm" icon={<IconArrowRight />} onClick={onCta} className="-mr-3">
          Open App Posture
        </Button>
      </div>
    </Tile>
  )
}

/* -------------------------------------------------------------------------- */
/* Secure Score top recommendations                                           */
/* -------------------------------------------------------------------------- */

function SecureScoreTopRecs({
  recs,
  summary,
  onOpen,
}: {
  recs: { rec: SecureScoreControlProfile; gap: number }[]
  summary: SecureScoreSummary | null
  onOpen: () => void
}) {
  const score = summary?.score?.percentage
  const columns: DataColumn<{ rec: SecureScoreControlProfile; gap: number }>[] = [
    {
      id: 'rec',
      header: 'Recommendation',
      primary: true,
      cell: ({ rec }) => <TableItem title={rec.title} sub={`${rec.controlCategory} · ${rec.service}`} />,
    },
    {
      id: 'gain',
      header: 'Points to gain',
      align: 'end',
      nowrap: true,
      // Microsoft publishes maxScore and achievedScore per control, so the
      // gap is the real Secure Score points this recommendation would add.
      cell: ({ gap }) => (
        <Tag size="sm" className="tabular-nums" title="Secure Score points to gain, estimated from Microsoft's published maximum">
          +{gap.toFixed(0)} pts
        </Tag>
      ),
    },
  ]
  return (
    <div className="min-w-0">
      <DataTable
        title="Secure Score top recommendations"
        description={
          score != null
            ? `Secure Score is at ${Math.round(score)}%. The biggest open gains come first.`
            : 'The biggest open gains come first.'
        }
        columns={columns}
        rows={recs}
        rowId={({ rec }) => rec.id}
        rowLabel={({ rec }) => rec.title}
        empty="Nothing to review."
        footer={
          <Button kind="ghost" size="sm" icon={<IconArrowRight />} onClick={onOpen} className="-ml-3">
            All recommendations
          </Button>
        }
      />
    </div>
  )
}
